/**
 * Testimonials Slider
 * 
 * Auto-sliding testimonials carousel showing 2 items at a time
 * 
 * @package CanadaAbroad
 */

(function() {
	'use strict';

	class TestimonialsSlider {
		constructor(container) {
			this.container = container;
			this.slidesContainer = container.querySelector('.testimonials-slider__container');
			this.slides = Array.from(container.querySelectorAll('.testimonials-slider__slide'));
			this.dots = Array.from(container.querySelectorAll('.testimonials-slider__dot'));
			this.prevBtn = container.querySelector('.testimonials-slider__nav--prev');
			this.nextBtn = container.querySelector('.testimonials-slider__nav--next');
			
			this.currentIndex = 0;
			this.slidesPerView = this.getSlidesPerView();
			this.totalSlides = this.slides.length;
			this.maxIndex = Math.max(0, this.totalSlides - 1);
			
			this.autoplayInterval = null;
			this.autoplayDelay = 5000; // 5 seconds
			this.isPaused = false;
			this.isTransitioning = false;
			
			this.init();
		}

		init() {
			if (this.totalSlides === 0) return;
			
			this.setupEventListeners();
			this.updateSlider();
			this.startAutoplay();
			this.handleResize();
		}

		getSlidesPerView() {
			return 1; // Always show 1 card at a time (centered)
		}

		handleResize() {
			let resizeTimer;
			window.addEventListener('resize', () => {
				clearTimeout(resizeTimer);
				resizeTimer = setTimeout(() => {
					this.updateSlider();
				}, 250);
			});
		}

		setupEventListeners() {
			// Navigation buttons
			if (this.prevBtn) {
				this.prevBtn.addEventListener('click', () => this.prev());
			}
			
			if (this.nextBtn) {
				this.nextBtn.addEventListener('click', () => this.next());
			}

			// Dots navigation
			this.dots.forEach((dot, index) => {
				dot.addEventListener('click', () => this.goToSlide(index));
			});

			// Pause on hover
			this.container.addEventListener('mouseenter', () => {
				this.pauseAutoplay();
			});

			this.container.addEventListener('mouseleave', () => {
				this.resumeAutoplay();
			});

			// Touch/swipe support
			this.setupTouchEvents();
		}

		setupTouchEvents() {
			let startX = 0;
			let startY = 0;
			let isDragging = false;

			this.container.addEventListener('touchstart', (e) => {
				startX = e.touches[0].clientX;
				startY = e.touches[0].clientY;
				isDragging = true;
				this.pauseAutoplay();
			});

			this.container.addEventListener('touchmove', (e) => {
				if (!isDragging) return;
				e.preventDefault();
			});

			this.container.addEventListener('touchend', (e) => {
				if (!isDragging) return;
				
				const endX = e.changedTouches[0].clientX;
				const endY = e.changedTouches[0].clientY;
				const diffX = startX - endX;
				const diffY = startY - endY;

				// Only swipe if horizontal movement is greater than vertical
				if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
					if (diffX > 0) {
						this.next();
					} else {
						this.prev();
					}
				}

				isDragging = false;
				this.resumeAutoplay();
			});
		}

		updateSlider() {
			if (this.isTransitioning) return;
			
			this.isTransitioning = true;
			
			// Calculate translateX to center the current slide
			const isDesktop = window.innerWidth >= 768;
			let translateX;
			
			if (isDesktop) {
				// Each slide is 60% width
				// To center a 60% card in a 100% container:
				// - Container center is at 50%
				// - Slide center needs to be at 50%
				// - Slide 0 starts at 0%, its center is at 30%, so move right by 20%
				// - Slide 1 starts at 60%, its center is at 90%, so move left by 40%: 60% - 40% = 20%
				// Formula: translateX = 20% - (currentIndex * 60%)
				translateX = 20 - (this.currentIndex * 60);
			} else {
				// Mobile: full width slides
				translateX = -(this.currentIndex * 100);
			}
			
			this.slidesContainer.style.transform = `translateX(${translateX}%)`;

			// Update active class on slides
			this.slides.forEach((slide, index) => {
				if (index === this.currentIndex) {
					slide.classList.add('active');
				} else {
					slide.classList.remove('active');
				}
			});

			// Update dots
			this.dots.forEach((dot, index) => {
				if (index === this.currentIndex) {
					dot.classList.add('active');
				} else {
					dot.classList.remove('active');
				}
			});

			// Update navigation buttons (allow looping)
			if (this.prevBtn) {
				this.prevBtn.style.opacity = '1';
				this.prevBtn.style.pointerEvents = 'auto';
			}

			if (this.nextBtn) {
				this.nextBtn.style.opacity = '1';
				this.nextBtn.style.pointerEvents = 'auto';
			}

			// Reset transition flag after animation
			setTimeout(() => {
				this.isTransitioning = false;
			}, 600);
		}

		next() {
			if (this.isTransitioning) return;
			
			if (this.currentIndex < this.maxIndex) {
				this.currentIndex++;
			} else {
				this.currentIndex = 0; // Loop back to start
			}
			this.updateSlider();
			this.resetAutoplay();
		}

		prev() {
			if (this.isTransitioning) return;
			
			if (this.currentIndex > 0) {
				this.currentIndex--;
			} else {
				this.currentIndex = this.maxIndex; // Loop to end
			}
			this.updateSlider();
			this.resetAutoplay();
		}

		goToSlide(index) {
			if (this.isTransitioning) return;
			
			this.currentIndex = Math.min(index, this.maxIndex);
			this.updateSlider();
			this.resetAutoplay();
		}

		startAutoplay() {
			if (this.autoplayInterval) {
				clearInterval(this.autoplayInterval);
			}

			this.autoplayInterval = setInterval(() => {
				if (!this.isPaused && !this.isTransitioning) {
					this.next();
				}
			}, this.autoplayDelay);
		}

		pauseAutoplay() {
			this.isPaused = true;
		}

		resumeAutoplay() {
			this.isPaused = false;
		}

		resetAutoplay() {
			this.startAutoplay();
		}

		destroy() {
			if (this.autoplayInterval) {
				clearInterval(this.autoplayInterval);
			}
		}
	}

	// Initialize slider when DOM is ready
	document.addEventListener('DOMContentLoaded', function() {
		const sliderContainers = document.querySelectorAll('.testimonials-slider');
		
		sliderContainers.forEach(container => {
			new TestimonialsSlider(container);
		});
	});

})();

